import {
  createContext, useCallback, useContext, useEffect, useId, useMemo, useState, type ReactNode,
} from 'react';
import {
  EXPECTED_ZONE_COUNT, HOME_ZONE, ZONES, consoleZoneLabel, resolveZonePreselection,
  useWorkingZoneCode, type PortalZone,
} from '../lib/zones';
import './zone-selector.css';

/**
 * ADR-123 Decision 1 — the zone in view is ALWAYS visible, on every page, in the navigation bar.
 *
 * ⭐⭐ WHY THIS LIVES IN THE CHROME AND NOT ON THE PAGE. UAT on 22 September 2026 could not tell
 * a page with no zone control from a page where the control had been hidden, or had broken.
 * Zone context that each page renders for itself is zone context a page can forget to render,
 * and "nothing on screen" reads the same whether it means "not applicable" or "misconfigured".
 * A single indicator in the navigation bar, mounted on every authenticated route and never
 * conditional on how many zones this bundle knows, removes that ambiguity by construction.
 *
 * Pages REPORT into it; they never render it. Each ADR-122 pattern has exactly one reporting
 * hook below, and the zone controls call the hook themselves where they can, so no page
 * hand-rolls a report:
 *
 *   - `useZoneReadReport`        — Patterns 1, 4, 5 and the reference pages (Customers, Alerts):
 *                                  the zones in view, per-zone row counts, the filter, failures.
 *   - `useZoneWriteReport`       — Pattern 3 and every create flow: the ONE zone a write lands in.
 *                                  Called by `useZoneSelection`.
 *   - `useZonePassthroughReport` — Pattern 2: the zone a single record was fetched from.
 *
 * A page that reports nothing is a global page (Staff, Geo Restrictions, My Profile, the
 * placeholders): the indicator still renders there — as chrome — saying "Not zone-scoped".
 */

export type ZoneReportMode = 'read' | 'write' | 'passthrough';

export interface ZoneInView {
  code: string;
  /** Rows this zone contributed, when the page has rows to count. */
  count?: number;
}

/** The platform-wide count's label — the Alerts board's own words (`ALL_ZONES_LABEL`). */
export const ALL_ZONES_SEGMENT = 'All zones';

export interface ZoneReport {
  mode: ZoneReportMode;
  /** read — every zone that answered, with its row count. */
  zonesInView: ZoneInView[];
  /** read — console labels of the zones that did not answer (from `failedZoneLabels`). */
  failedZones: string[];
  /** read — `'all'` or the zone code the list is narrowed to. */
  filter: 'all' | string;
  /** write / passthrough — the single zone in play. */
  targetZone?: string;
  /**
   * read — names a SECOND read on the same screen (e.g. the person-access panel inside the
   * subscriber drawer, over the subscriber list) so two sets of counts are not read as one.
   */
  scope?: string;
  /**
   * read — rows that belong to NO single zone (a platform-wide alert, raised once about a service
   * every zone uses). Counted once here and never added to the zone whose table stores them, so
   * the bar and the Alerts board's legend say the same thing (Jeff, 11 October 2026: "agreed").
   * Absent when the page has no platform-wide concept.
   */
  platformCount?: number;
}

interface ZoneContextValue {
  reports: ZoneReport[];
  set: (id: string, report: ZoneReport | null) => void;
}

const ZoneCtx = createContext<ZoneContextValue | null>(null);

export function ZoneContextProvider({ children }: { children: ReactNode }) {
  const [byId, setById] = useState<Record<string, ZoneReport>>({});
  // ⚠ `set` MUST BE STABLE. Reporters list it as an effect dependency; if it changed whenever a
  // report did, every reporter would unregister and re-register on every report — an infinite
  // render loop (it was, in the first draft).
  const set = useCallback((id: string, report: ZoneReport | null) =>
    setById((prev) => {
      if (report === null) {
        if (!(id in prev)) return prev;
        const rest = { ...prev };
        delete rest[id];
        return rest;
      }
      // Identical content is not a change — without this a report rebuilt on every render
      // would loop render → effect → setState → render.
      if (prev[id] && JSON.stringify(prev[id]) === JSON.stringify(report)) return prev;
      return { ...prev, [id]: report };
    }), []);
  const value = useMemo<ZoneContextValue>(() => ({ reports: Object.values(byId), set }), [byId, set]);
  return <ZoneCtx.Provider value={value}>{children}</ZoneCtx.Provider>;
}

/**
 * Register a report for the lifetime of the calling component. Outside a provider (a component
 * rendered by a unit test on its own) it is a no-op — reporting is chrome, and a component must
 * never fail to render because nothing is listening.
 */
function useZoneReport(report: ZoneReport | null): void {
  const ctx = useContext(ZoneCtx);
  const id = useId();
  const key = report ? JSON.stringify(report) : '';
  const set = ctx?.set;
  useEffect(() => {
    if (!set) return undefined;
    set(id, key ? (JSON.parse(key) as ZoneReport) : null);
    return () => set(id, null);
  }, [set, id, key]);
}

/**
 * Patterns 1 / 4 / 5 and the reference pages. Pass the page's UNFILTERED rows (each tagged with
 * its zone by the fan-out) so the counts describe what the zones returned, not what the filter
 * leaves; pass `null` rows while the first load is in flight to report zones without counts.
 * An untagged row counts toward the home zone — the same fallback `zoneByCode` applies to it.
 */
export function useZoneReadReport({
  rows,
  failedZones,
  filter = 'all',
  counted = true,
  scope,
  isPlatformWide,
}: {
  rows: ReadonlyArray<{ zone?: string }> | null;
  failedZones: string[];
  filter?: string;
  scope?: string;
  /** False where rows are not the unit an operator counts (e.g. Monitoring's vendor totals). */
  counted?: boolean;
  /**
   * The page's OWN platform-wide flag (the Alerts board passes `isPlatformWide` from
   * `lib/alerts-zones.ts` — the same predicate its legend uses, never a re-derivation). Matching
   * rows are counted once as "All zones" and excluded from every zone's count.
   */
  isPlatformWide?: (row: { zone?: string }) => boolean;
}): void {
  const zoned = rows && isPlatformWide ? rows.filter((r) => !isPlatformWide(r)) : rows;
  const zonesInView: ZoneInView[] = ZONES
    .filter((z) => !failedZones.includes(z.label))
    .map((z) => ({
      code: z.code,
      ...(counted && zoned
        ? { count: zoned.filter((r) => (r.zone ?? HOME_ZONE.code) === z.code).length }
        : {}),
    }));
  useZoneReport({
    mode: 'read', zonesInView, failedZones, filter: filter as 'all' | string,
    ...(scope ? { scope } : {}),
    ...(counted && rows && isPlatformWide ? { platformCount: rows.length - (zoned?.length ?? 0) } : {}),
  });
}

/** The write target meaning "every zone at once" — a platform-wide setting (the availability
 *  gates: one global KV key, ADR-120 P2). */
export const EVERY_ZONE = '*';

/** Pattern 3 and create flows — the single zone a write will land in (or `EVERY_ZONE`). */
export function useZoneWriteReport(targetZone: string | null): void {
  useZoneReport(targetZone
    ? { mode: 'write', zonesInView: [{ code: targetZone }], failedZones: [], filter: targetZone, targetZone }
    : null);
}

/** Pattern 2 — the zone the record on screen was fetched from. */
export function useZonePassthroughReport(targetZone: string | null): void {
  useZoneReport(targetZone
    ? { mode: 'passthrough', zonesInView: [{ code: targetZone }], failedZones: [], filter: targetZone, targetZone }
    : null);
}

/**
 * Pattern 2 in one call — resolve which zone a detail page asks for its one record, and report
 * it. An explicit `?zone=` wins (the list that linked here knew); a link with none asks the
 * staff user's preferred zone, else home (ADR-123 Decision 3), and the page's
 * `ZoneAmbiguousNotFound` offers the others if that zone does not hold it.
 */
export function usePassthroughZone(urlZone: string | null | undefined): PortalZone {
  // ADR-124 A2 — the working zone (preferred until switched); an explicit `?zone=` still wins.
  const preferred = useWorkingZoneCode();
  const zone = resolveZonePreselection({ urlZone, preferred, zones: ZONES });
  useZonePassthroughReport(zone.code);
  return zone;
}

/**
 * A page that still reads ONLY the home zone (Growth, whose zone scoping is a pending product
 * decision under ADR-122; Dashboard Access used this until it fanned out, 25 September 2026). Reported as a read narrowed to the home zone, so
 * the navigation bar says "ap-southeast-2 · Australia only" — the truth — rather than the
 * "Not zone-scoped" a report-less page gets, which here would be false.
 */
export function useZoneHomeOnlyReport(): void {
  useZoneReport({
    mode: 'read', zonesInView: [{ code: HOME_ZONE.code }], failedZones: [], filter: HOME_ZONE.code,
  });
}

function readSegment(r: ZoneReport): string {
  const body = r.filter !== 'all'
    ? `${consoleZoneLabel(r.filter)} only`
    : r.zonesInView
      .map((z) => (z.count === undefined ? consoleZoneLabel(z.code) : `${consoleZoneLabel(z.code)} ${z.count}`))
      .concat(r.platformCount === undefined ? [] : [`${ALL_ZONES_SEGMENT} ${r.platformCount}`])
      .join(' · ');
  return r.scope ? `${r.scope}: ${body}` : body;
}

/** The indicator's text, one entry per segment — exported so the wording is testable directly. */
export function zoneIndicatorSegments(reports: ZoneReport[]): string[] {
  const out: string[] = [];
  const push = (s: string) => { if (s && !out.includes(s)) out.push(s); };
  for (const r of reports.filter((x) => x.mode === 'read')) push(readSegment(r));
  for (const r of reports.filter((x) => x.mode === 'passthrough')) push(`Record from ${consoleZoneLabel(r.targetZone!)}`);
  for (const r of reports.filter((x) => x.mode === 'write')) {
    push(r.targetZone === EVERY_ZONE
      ? 'Editing every zone · platform-wide'
      : `Editing ${consoleZoneLabel(r.targetZone!)}`);
  }
  if (!out.length) {
    push(`Not zone-scoped · ${ZONES.length} ${ZONES.length === 1 ? 'zone' : 'zones'} configured`);
  }
  return out;
}

/**
 * The navigation-bar indicator. Rendered by `AppHeader` on every authenticated route,
 * unconditionally.
 */
export function ZoneIndicator() {
  const ctx = useContext(ZoneCtx);
  const reports = ctx?.reports ?? [];
  const segments = zoneIndicatorSegments(reports);
  const failed = [...new Set(reports.flatMap((r) => r.failedZones))];
  const misconfigured = ZONES.length < EXPECTED_ZONE_COUNT;
  return (
    <div className="zone-indicator" role="status" aria-label="Zone in view" data-testid="zone-indicator">
      {misconfigured && (
        <span className="zone-indicator-warn">
          {ZONES.length} {ZONES.length === 1 ? 'zone' : 'zones'} configured — expected {EXPECTED_ZONE_COUNT}
        </span>
      )}
      {segments.map((s) => (
        <span key={s} className="zone-indicator-seg">{s}</span>
      ))}
      {failed.map((label) => (
        <span key={label} className="zone-indicator-warn">{label} did not respond</span>
      ))}
    </div>
  );
}
